/**
 * Subscription Step Context
 * 
 * Manages navigation between subscription purchase flow steps with validation.
 * 
 * Steps:
 * 0. Plan Selection (memberships or passes)
 * 1. Review Step (review selected plan)
 * 2. Complete Account (guest users only)
 * 
 * Similar to BookingStepContext pattern.
 * 
 * Note: Fast Refresh warnings about exporting non-components are expected.
 * This pattern matches BookingStepContext which keeps everything in one file.
 * 
 * @module subscription/context
 */

/* eslint-disable react-refresh/only-export-components */

import { 
  createContext, 
  useState, 
  useCallback, 
  useContext,
  type ReactNode 
} from 'react';
import { useSubscriptionPurchase } from './SubscriptionPurchaseContext';

/**
 * Step IDs for type safety
 */
export const SUBSCRIPTION_STEPS = {
  PLAN_SELECTION: 0,
  REVIEW: 1,
  COMPLETE_ACCOUNT: 2,
} as const;

export type SubscriptionStepId = typeof SUBSCRIPTION_STEPS[keyof typeof SUBSCRIPTION_STEPS];

/**
 * Subscription Step Context Value
 */
export interface SubscriptionStepContextValue {
  /** Current step index (0-based) */
  currentStep: SubscriptionStepId;
  /** Total number of steps */
  totalSteps: number;
  /** Navigate to a specific step (with validation) */
  goToStep: (step: SubscriptionStepId) => boolean;
  /** Go to next step (with validation) */
  goToNextStep: () => boolean;
  /** Go to previous step */
  goToPreviousStep: () => void;
  /** Check if user can navigate to a specific step */
  canGoToStep: (step: SubscriptionStepId) => boolean;
  /** Check if current step is valid */
  isCurrentStepValid: () => boolean;
}

/**
 * Subscription Step Context
 */
export const SubscriptionStepContext = createContext<SubscriptionStepContextValue | null>(null);

/**
 * Props for SubscriptionStepProvider
 */
interface SubscriptionStepProviderProps {
  children: ReactNode;
  /** Initial step (defaults to PLAN_SELECTION) */
  initialStep?: SubscriptionStepId;
}

/**
 * Subscription Step Provider
 * 
 * Manages step navigation with validation logic.
 * 
 * @example
 * ```tsx
 * <SubscriptionStepProvider>
 *   <SubscriptionPurchaseFlowPage />
 * </SubscriptionStepProvider>
 * ```
 */
export function SubscriptionStepProvider({ 
  children, 
  initialStep = SUBSCRIPTION_STEPS.PLAN_SELECTION 
}: SubscriptionStepProviderProps) {
  const [currentStep, setCurrentStepRaw] = useState<SubscriptionStepId>(initialStep);
  const { selectedSubscription, guestUser } = useSubscriptionPurchase();

  /** Set step and scroll to top so the user always starts at the top of the new step */
  const setCurrentStep = useCallback((step: SubscriptionStepId) => {
    setCurrentStepRaw(step);
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, []);
  
  // Total steps depends on whether user is a guest
  const totalSteps = guestUser ? 3 : 2;
  
  /**
   * Validate if a step can be accessed
   */
  const canGoToStep = useCallback((step: SubscriptionStepId): boolean => {
    // Always allow going back to plan selection
    if (step === SUBSCRIPTION_STEPS.PLAN_SELECTION) {
      return true;
    }
    
    // Review step requires a selected subscription
    if (step === SUBSCRIPTION_STEPS.REVIEW) {
      return selectedSubscription !== null;
    }
    
    // Complete account step requires subscription + guest user
    if (step === SUBSCRIPTION_STEPS.COMPLETE_ACCOUNT) {
      return selectedSubscription !== null && guestUser !== null;
    }
    
    return false;
  }, [selectedSubscription, guestUser]);
  
  /**
   * Check if current step is valid and allows progression
   */
  const isCurrentStepValid = useCallback((): boolean => {
    switch (currentStep) {
      case SUBSCRIPTION_STEPS.PLAN_SELECTION:
        // Valid if a plan is selected
        return selectedSubscription !== null;
      
      case SUBSCRIPTION_STEPS.REVIEW:
        // Valid if we have a subscription
        return selectedSubscription !== null;
      
      case SUBSCRIPTION_STEPS.COMPLETE_ACCOUNT:
        // Valid if guest user data is present
        return guestUser !== null && selectedSubscription !== null;
      
      default:
        return false;
    }
  }, [currentStep, selectedSubscription, guestUser]);
  
  /**
   * Navigate to a specific step with validation
   */
  const goToStep = useCallback((step: SubscriptionStepId): boolean => {
    if (!canGoToStep(step)) {
      console.warn('[SubscriptionStepContext] Cannot navigate to step', {
        targetStep: step,
        currentStep,
        selectedSubscription: !!selectedSubscription,
        guestUser: !!guestUser,
      });
      return false;
    }
    
    if (import.meta.env.DEV) {
      console.log('[SubscriptionStepContext] Navigating to step', {
        from: currentStep,
        to: step,
      });
    }
    
    setCurrentStep(step);
    return true;
  }, [currentStep, canGoToStep, selectedSubscription, guestUser, setCurrentStep]);
  
  /**
   * Go to next step with validation
   */
  const goToNextStep = useCallback((): boolean => {
    if (!isCurrentStepValid()) {
      console.warn('[SubscriptionStepContext] Cannot proceed - current step invalid');
      return false;
    }
    
    let nextStep: SubscriptionStepId;
    
    switch (currentStep) {
      case SUBSCRIPTION_STEPS.PLAN_SELECTION:
        nextStep = SUBSCRIPTION_STEPS.REVIEW;
        break;
      
      case SUBSCRIPTION_STEPS.REVIEW:
        // If guest user, go to complete account, otherwise done
        if (guestUser) {
          nextStep = SUBSCRIPTION_STEPS.COMPLETE_ACCOUNT;
        } else {
          // Authenticated users would proceed to payment here
          console.log('[SubscriptionStepContext] Authenticated user - proceed to payment');
          return false;
        }
        break;
      
      case SUBSCRIPTION_STEPS.COMPLETE_ACCOUNT:
        // This is the last step before payment
        console.log('[SubscriptionStepContext] Complete account - proceed to payment');
        return false;
      
      default:
        return false;
    }
    
    return goToStep(nextStep);
  }, [currentStep, isCurrentStepValid, goToStep, guestUser]);
  
  /**
   * Go to previous step
   */
  const goToPreviousStep = useCallback(() => {
    let prevStep: SubscriptionStepId;
    
    switch (currentStep) {
      case SUBSCRIPTION_STEPS.REVIEW:
        prevStep = SUBSCRIPTION_STEPS.PLAN_SELECTION;
        break;
      
      case SUBSCRIPTION_STEPS.COMPLETE_ACCOUNT:
        prevStep = SUBSCRIPTION_STEPS.REVIEW;
        break;
      
      case SUBSCRIPTION_STEPS.PLAN_SELECTION:
      default:
        // Already at first step
        return;
    }
    
    if (import.meta.env.DEV) {
      console.log('[SubscriptionStepContext] Going back to step', prevStep);
    }
    
    setCurrentStep(prevStep);
  }, [currentStep, setCurrentStep]);
  
  const value: SubscriptionStepContextValue = {
    currentStep,
    totalSteps,
    goToStep,
    goToNextStep,
    goToPreviousStep,
    canGoToStep,
    isCurrentStepValid,
  };
  
  return (
    <SubscriptionStepContext.Provider value={value}>
      {children}
    </SubscriptionStepContext.Provider>
  );
}

/**
 * Hook to use subscription step context
 * 
 * @throws Error if used outside SubscriptionStepProvider
 */
export function useSubscriptionStep(): SubscriptionStepContextValue {
  const context = useContext(SubscriptionStepContext);
  
  if (!context) {
    throw new Error('useSubscriptionStep must be used within SubscriptionStepProvider');
  }
  
  return context;
}
